import { StringValueSchema, toExpandedObject } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { get, isEqual, kebabCase, omit } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import { useDeepCompareMemoize } from "use-deep-compare-effect";
import { Accordion } from "../Accordion";
import { Button } from "../Button";
import { ButtonWithPopover } from "../ButtonWithPopover";
import DataTable from "../DataTable/DataTable";
import { DataTableEmptyState } from "../DataTable/DataTableEmptyState";
import {
  DataTableProps,
  type DataTableDensity,
  type HeaderType,
  type ItemBaseType,
} from "../DataTable/types";
import { DropdownMenu } from "../DropdownMenu";
import { Icon, IconLoading } from "../Icon";
import type { IconMapping } from "../Icon/Icon.mapping";
import { Pagination } from "../Pagination";
import { DefaultPageSizeOptions } from "../Pagination/Pagination.constants";
import Panel from "../Panel/Panel";
import { PanelProps } from "../Panel/types";
import { SelectPopover, type SelectPopoverOptionType } from "../SelectPopover";
import type { GroupingType, SortingType } from "../utils/component-props";
import { usePrevious } from "../utils/hooks/usePrevious";
import { useStickyState } from "../utils/hooks/useStickyState";
import { DataTableColumnConfigurationDrawer } from "./DataTableColumnConfigurationDrawer";
import {
  areItemsInSelection,
  checkIfHeaderConfigsValid,
  checkIfHeadersConfigured,
  getDataTableGroupByValueAndKeyForGroupByEntry,
  getHeaderConfigFromHeaders,
  getHeaderConfigsFromOptions,
  getHeadersFromHeaderConfigs,
  getOptionsFromHeaderConfigs,
  getSelectedHeaderKeysFromHeaderConfig,
  getSelectedHeaderKeysFromHeaders,
  makeGroupByMapFromHeaders,
  makeMapFromHeaderConfigs,
  makeMapFromHeaders,
  mergeHeaderConfigs,
  type ConfiguredDataTable,
  type ConfiguredDataTableHeader,
} from "./DataTablePanel.helpers";
import styles from "./DataTablePanel.module.scss";

const cx = classNames.bind(styles);
const { AccordionPanel } = Accordion;

export type DataTableColumnConfigurationProps<ItemType extends ItemBaseType> = {
  headers: Array<HeaderType<ItemType>>;
  configuredHeaders: ConfiguredDataTableHeader[];
  headersMap: Map<string, HeaderType<ItemType>>;
  initialHeadersConfig: ConfiguredDataTableHeader[];
  onChangeColumnConfiguration: (
    newConfig: Pick<ConfiguredDataTable, "headers" | "source">
  ) => void;
  onResetColumnConfiguration?: () => Promise<
    ConfiguredDataTableHeader[] | undefined
  >;
  onSaveColumnConfigurationAsDefault?: (
    columnsToSave: ConfiguredDataTableHeader[]
  ) => Promise<boolean>;
  isSaveDefaultColumnConfigurationAvailable?: boolean;
  isLoadingSaveDefaultColumnConfiguration?: boolean;
  onDeleteDefaultColumnConfiguration?: () => Promise<boolean>;
  isDeleteDefaultColumnConfigurationAvailable?: boolean;
  isLoadingDeleteDefaultColumnConfiguration?: boolean;
  isDefaultColumnConfigurationAvailable?: boolean;
  isCompanyColumnConfigurationAvailable?: boolean;
  defaultColumnConfiguration?: ConfiguredDataTableHeader[];
  companyDefaultColumnConfiguration?: ConfiguredDataTableHeader[];
  personalDefaultColumnConfiguration?: ConfiguredDataTableHeader[];
  isLoadingDefaultColumnConfiguration?: boolean;
};

export type DataTablePanelProps<ItemType extends ItemBaseType> = {
  storageKey?: string;
  panelProps?: Omit<PanelProps, "children">;
  dataTableProps: DataTableProps<ItemType>;
  filterComponent?: React.ReactNode;
  columnConfigurationProps?: Omit<
    DataTableColumnConfigurationProps<ItemType>,
    | "headers"
    | "headersMap"
    | "configuredHeaders"
    | "initialHeadersConfig"
    | "onChangeColumnConfiguration"
  >;
  groupBy?: GroupingType["groupBy"];
  onGroupByChange?: (groupByState: GroupingType & Partial<SortingType>) => void;
  groupByLimit?: number;
  groupByTablesProps?: Array<DataTableProps<ItemType>>;
  defaultActiveGroupByGroups?: string[];
  onActiveGroupByGroupsChange?: (activeGroupByGroups: string[]) => void;
};

export const StorageType = {
  tableConfig: "table-configuration",
  filterConfig: "filter-configuration",
  viewConfig: "view-configuration",
  tableState: "table-state",
  groupByTablesState: "group-by-tables-state",
} as const;

export type StorageKeys = Record<
  `${keyof typeof StorageType}StorageKey`,
  string
>;

const densityToIconNameMap: Record<DataTableDensity, keyof typeof IconMapping> =
  {
    compact: "lines-horizontal-four",
    normal: "lines-horizontal-three",
    relaxed: "lines-horizontal-two",
  };
const GROUP_BY_ONLY_SORT_VALUES = new Set(["value", "count"]);

/**
 * DataTable wrapped in a Panel which comes rolled up with space for table
 * actions (with selections or without), column configuration, and in the near
 * future, table filters.
 *
 * Regarding Column Configuration, there are 3 distinct data representation
 * formats that are frequently dealth with and translated interchangeably:
 *
 * 1. the format in which headers are defined (HeaderType)
 * 2. the format in which options for the SelectPopover are defined
 * 3. the format in which headers are persisted in localStorage
 *
 * In relation to this, it's good to know the recursive column definition and UI
 * behavior of the DataTable component.
 */
export const DataTablePanel = <ItemType extends ItemBaseType>({
  storageKey,
  panelProps,
  dataTableProps,
  filterComponent,
  columnConfigurationProps,
  groupBy = [],
  onGroupByChange,
  groupByLimit = 1,
  groupByTablesProps,
  defaultActiveGroupByGroups = [],
  onActiveGroupByGroupsChange,
}: DataTablePanelProps<ItemType>) => {
  const { actionRow } = panelProps ?? {};
  const { density = "normal", headers, isLoading } = dataTableProps ?? {};

  const prevHeaders = usePrevious(headers);
  const groupableHeaders = useMemo<Array<HeaderType<ItemType>>>(
    () => headers?.filter((h) => h.isGroupable) ?? [],
    [headers]
  );
  const [densitySetting, setDensitySetting] = useState(density);
  const headersMap = useMemo(() => {
    return makeMapFromHeaders<ItemType>(headers ?? []);
  }, [headers, isLoading]);
  const groupByHeadersMap = useMemo(() => {
    return makeGroupByMapFromHeaders<ItemType>(headers ?? []);
  }, [headers, isLoading]);
  const initialHeadersConfig = useMemo(
    () => getHeaderConfigFromHeaders(headers ?? []),
    [headers]
  );
  const [configuredTable, setConfiguredTable] =
    useStickyState<ConfiguredDataTable>(
      {
        source: "code",
        headers: initialHeadersConfig,
      },
      storageKey !== undefined
        ? storageKey?.includes(StorageType.tableConfig)
          ? storageKey
          : `${StorageType.tableConfig}-${kebabCase(storageKey)}`
        : typeof panelProps?.title === "string"
          ? `${StorageType.tableConfig}-${kebabCase(
              typeof panelProps?.title === "string" ? panelProps?.title : ""
            )}`
          : StorageType.tableConfig
    );

  // normalize stale persisted sorting that is only valid when grouping is active.
  useEffect(() => {
    if (!onGroupByChange || groupBy.length) return;

    if (!GROUP_BY_ONLY_SORT_VALUES.has(dataTableProps.sorting?.sortBy ?? "")) {
      return;
    }

    onGroupByChange({
      groupBy: [],
      sortBy: undefined,
    });
  }, [onGroupByChange, groupBy, dataTableProps.sorting?.sortBy]);

  const getConfiguredTableHeaders = (
    configuration: ConfiguredDataTable | ConfiguredDataTableHeader[]
  ) => {
    // migration from the old format to the new one (may 2025)
    if (Array.isArray(configuration)) {
      setConfiguredTable({
        source: "code",
        headers:
          (configuration as unknown as ConfiguredDataTableHeader[]) || [],
      });
      return (configuration as unknown as ConfiguredDataTableHeader[]) || [];
    } else {
      return configuration?.headers;
    }
  };
  const configuredHeaders = useDeepCompareMemoize(
    getConfiguredTableHeaders(configuredTable)
  );
  const configurationType = configuredTable?.source ?? "code";
  const prevConfiguredHeaders = usePrevious(configuredHeaders);
  // if headers haven't been configured before or the configuration stored
  // doesn't match the provided headers - treat provided headers as the truth
  const areHeadersConfigured = useMemo(
    () => checkIfHeadersConfigured(initialHeadersConfig, configuredHeaders),
    [initialHeadersConfig, configuredHeaders]
  );

  useEffect(() => {
    // on first load prevHeaders will be undefined. when headers are still
    // loading, prevHeaders and headers won't match - probably a good idea to
    // fix the root cause which is ensuring that the loading state for the table
    // is wired up properly.
    if (isLoading || !prevHeaders || !storageKey) return;

    const configuredHeadersMap = makeMapFromHeaderConfigs(configuredHeaders);
    const areConfiguredHeadersValid = checkIfHeaderConfigsValid(
      configuredHeadersMap,
      headersMap
    );

    if (
      ((configurationType === "user-default" || configurationType === "user") &&
        !areConfiguredHeadersValid) ||
      (configurationType === "code" &&
        // code definition needs to be exactly the same, including order
        !isEqual(
          Array.from(configuredHeadersMap.keys()),
          Array.from(headersMap.keys())
        ))
    ) {
      // if there is a discrepancy in the configuration between what is persisted
      // in local storage and what the headers defined in the application (+
      // accounting for dynamic user-based data) dictate, then reset to the ones
      // defined by the application
      setConfiguredTable({
        headers: initialHeadersConfig,
        source: "code",
      });
    }
  }, [
    storageKey,
    prevConfiguredHeaders,
    prevHeaders,
    headers,
    headersMap,
    initialHeadersConfig,
    isLoading,
  ]);

  const groupByAccordionPanels = useMemo(() => {
    return (
      dataTableProps?.items as unknown as Array<{
        value: unknown;
        count: number;
      }>
    )?.map((groupedEntry, idx) => {
      const [groupByValueSafe, groupByKeySafe] =
        getDataTableGroupByValueAndKeyForGroupByEntry(
          groupedEntry as unknown as { value: unknown; count: number }
        );
      const isValueALookup =
        typeof groupByValueSafe === "string" &&
        StringValueSchema.uuid().safeParse(groupByValueSafe).success;

      const isLoading = isValueALookup
        ? groupByTablesProps?.[idx]?.isLoading
        : false;
      const isBusy = isValueALookup ? groupByTablesProps?.[idx]?.isBusy : false;
      const hasSelection = areItemsInSelection(
        dataTableProps.selected ?? {},
        groupByTablesProps?.[idx]?.items ?? []
      );
      const [renderedGroupByKey, renderFunction] = (() => {
        for (const groupByKey of groupBy) {
          if (!headersMap.has(groupByKey) && !groupByHeadersMap.has(groupByKey))
            continue;

          return [
            groupByKey,
            groupByHeadersMap.get(groupByKey)?.renderComponent ??
              headersMap.get(groupByKey)?.renderComponent,
          ];
        }

        return [];
      })();

      return (
        <AccordionPanel
          key={groupByKeySafe}
          dataKey={groupByKeySafe ?? "-"}
          variant="bordered"
          title={
            <>
              {renderFunction?.({
                value: isValueALookup
                  ? (get(groupByTablesProps?.[idx]?.items[0], groupBy[0]) ??
                    groupByValueSafe)
                  : groupByValueSafe,
                item: isValueALookup
                  ? (groupByTablesProps?.[idx]?.items[0] ??
                    (toExpandedObject({
                      ...omit(groupedEntry, ["value", "count"]),
                      [groupBy[0]]: groupByValueSafe,
                    }) as ItemType))
                  : (toExpandedObject({
                      ...omit(groupedEntry, ["value", "count"]),
                      [groupBy[0]]: groupByValueSafe,
                    }) as ItemType),
                dataKey: renderedGroupByKey ?? groupByKeySafe ?? "-",
                idx,
                alignment: "left",
                isLoading,
                isBusy,
                opts: {
                  emptyText: "(Blank)",
                },
              }) ?? "(Blank)"}
              <p className={cx("grouped-entry-count")}>{groupedEntry.count}</p>
              {hasSelection ? (
                <div
                  className={cx("indicator", "is-inline")}
                  role="presentation"
                  aria-label="Selections have been made in this group"
                  style={{ height: 12, width: 12 }}
                />
              ) : null}
            </>
          }
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <DataTable
            {...groupByTablesProps?.[idx]}
            items={groupByTablesProps?.[idx]?.items ?? []}
            getItemId={
              groupByTablesProps?.[idx]?.getItemId ??
              ((item) => (item?.id as string) ?? "")
            }
            density={densitySetting}
            headers={
              (areHeadersConfigured
                ? getHeadersFromHeaderConfigs(configuredHeaders, headersMap)
                : getHeadersFromHeaderConfigs(
                    initialHeadersConfig,
                    headersMap
                  )) ?? []
            }
          />
        </AccordionPanel>
      );
    });
  }, [
    dataTableProps.items,
    groupByTablesProps,
    headersMap,
    groupByHeadersMap,
    areHeadersConfigured,
    initialHeadersConfig,
    configuredHeaders,
  ]);

  const groupBySelectPopoverOptions = useMemo(
    () => [
      {
        label: "None",
        value: "none",
        isPinned: true,
        options: undefined,
      },
      ...groupableHeaders.map<SelectPopoverOptionType>((h) => {
        return {
          label: h.label ?? "",
          value: h.groupByKeys?.join(":") ?? h.key,
          options: undefined,
          slotRight: ({ iconProps }) =>
            groupBy.includes(h.groupByKeys?.join(":") ?? "") ||
            groupBy.includes(h.key) ? (
              <Icon
                {...iconProps}
                variant={
                  dataTableProps.sorting?.sortDirection === "desc"
                    ? "arrow-down"
                    : "arrow-up"
                }
                alt={
                  dataTableProps.sorting?.sortDirection === "desc"
                    ? "Sorted descending. Click to sort ascending."
                    : "Sorted ascending. Click to sort descending."
                }
              />
            ) : null,
        };
      }),
    ],
    [groupableHeaders, dataTableProps.sorting?.sortDirection, groupBy]
  );

  const panelActionDependencies = useDeepCompareMemoize([
    densitySetting,
    areHeadersConfigured,
    headers,
    configuredHeaders,
    initialHeadersConfig,
    headersMap,
    columnConfigurationProps,
    setConfiguredTable,
    groupBy,
    dataTableProps.sorting?.sortDirection,
  ]);

  const panelActionRow = useMemo(() => {
    const modifiedActionRow: React.ReactNode[] = [];
    const tableConfigActions: React.ReactNode[] = [
      filterComponent,
      groupableHeaders.length ? (
        <SelectPopover
          key="grouping"
          defaultValue={groupBy.length ? groupBy : ["none"]}
          searchProps={{
            placeholder: "Search columns...",
          }}
          isMulti={groupByLimit > 1}
          options={groupBySelectPopoverOptions}
          onChange={(newGroupBySelection) => {
            if (newGroupBySelection.includes("none")) {
              onGroupByChange?.({ groupBy: [], sortBy: undefined });
              return;
            }

            const actualGroupBySelection = newGroupBySelection.flatMap((g) =>
              g.includes(":") ? g.split(":") : [g]
            );
            const relatedHeader = newGroupBySelection.length
              ? groupableHeaders.find((h) =>
                  newGroupBySelection[0].includes(":")
                    ? h.groupByKeys?.join(":") === newGroupBySelection[0]
                    : h.key === newGroupBySelection[0]
                )
              : undefined;

            // if the sort by being applied is not in the new group by selection
            // then sort by special string "value" instead which are the first
            // group by properties' values, otherwise remove any sorting
            const newSortBy = relatedHeader?.groupBySortByKey
              ? relatedHeader.groupBySortByKey
              : actualGroupBySelection.length &&
                  !actualGroupBySelection.includes(
                    dataTableProps.sorting?.sortBy ?? ""
                  )
                ? "value"
                : undefined;

            onGroupByChange?.({
              groupBy: actualGroupBySelection,
              ...(newSortBy ? { sortBy: newSortBy } : {}),
              // intentionally flip the sort direction on every change, when the
              // same group by is clicked, otherwise, default to ascending
              sortDirection:
                actualGroupBySelection.length === groupBy.length &&
                actualGroupBySelection.every((g) => groupBy.includes(g))
                  ? dataTableProps.sorting?.sortDirection === "asc"
                    ? "desc"
                    : "asc"
                  : "asc",
            });
          }}
        >
          <div className={cx("action-container")}>
            <ButtonWithPopover
              label="Grouping"
              buttonProps={{
                icon: "text-indent",
              }}
            />
            {groupBy.length ? (
              <div
                className={cx("indicator")}
                role="presentation"
                aria-label="Column grouping has been applied"
              />
            ) : null}
          </div>
        </SelectPopover>
      ) : null,
      <TableColumnConfigurationAction<ItemType>
        key="column-config"
        headers={headers ?? []}
        configuredHeaders={configuredHeaders}
        configuredTableSource={configurationType}
        headersMap={headersMap}
        initialHeadersConfig={initialHeadersConfig}
        areHeadersConfigured={areHeadersConfigured}
        onChangeColumnConfiguration={(config) =>
          setConfiguredTable((prevConfig) => ({ ...prevConfig, ...config }))
        }
        {...columnConfigurationProps}
      />,
      <DropdownMenu
        key="settings"
        options={[
          {
            label: "Density",
            slotLeft: ({ iconProps }) => (
              <Icon
                {...iconProps}
                variant="lines-horizontal-three"
              />
            ),
            options: [
              {
                variant: densitySetting === "relaxed" ? "highlight" : "default",
                label: "Loose",
                slotLeft: ({ iconProps }) => (
                  <Icon
                    {...iconProps}
                    variant={densityToIconNameMap.relaxed}
                  />
                ),
                slotRight: ({ iconProps }) =>
                  densitySetting === "relaxed" && (
                    <Icon
                      {...iconProps}
                      variant="check-circle"
                      size={16}
                    />
                  ),
                onClick: () => setDensitySetting("relaxed"),
              },
              {
                variant: densitySetting === "normal" ? "highlight" : "default",
                label: "Normal",
                slotLeft: ({ iconProps }) => (
                  <Icon
                    {...iconProps}
                    variant={densityToIconNameMap.normal}
                  />
                ),
                slotRight: ({ iconProps }) =>
                  densitySetting === "normal" && (
                    <Icon
                      {...iconProps}
                      variant="check-circle"
                      size={16}
                    />
                  ),
                onClick: () => setDensitySetting("normal"),
              },
              {
                variant: densitySetting === "compact" ? "highlight" : "default",
                label: "Tight",
                slotLeft: ({ iconProps }) => (
                  <Icon
                    {...iconProps}
                    variant={densityToIconNameMap.compact}
                  />
                ),
                slotRight: ({ iconProps }) =>
                  densitySetting === "compact" && (
                    <Icon
                      {...iconProps}
                      variant="check-circle"
                      size={16}
                    />
                  ),
                onClick: () => setDensitySetting("compact"),
              },
            ],
          },
        ]}
      >
        <ButtonWithPopover
          label="Table Settings"
          buttonProps={{
            icon: "gear-six",
            iconProps: {
              size: 18,
            },
            size: "small",
          }}
        />
      </DropdownMenu>,
    ];

    if (React.isValidElement(actionRow) || Array.isArray(actionRow)) {
      modifiedActionRow.push(
        <div className={cx("user-actions")}>{actionRow}</div>
      );
    }

    // if user defined actions exist and table config actions exist, add a divider
    if (modifiedActionRow.length && tableConfigActions.length) {
      modifiedActionRow.push(
        <div
          key="divider"
          className={cx("actionDivider")}
          role="presentation"
        />
      );
    }

    // always add the table config actions
    if (tableConfigActions.length) {
      modifiedActionRow.push(
        <div className={cx("config-actions")}>{tableConfigActions}</div>
      );
    }

    return modifiedActionRow;
  }, [actionRow, filterComponent, panelActionDependencies]);

  // grouped tables have a root pagination component
  const showPagination =
    groupBy.length &&
    dataTableProps?.pagination?.page !== undefined &&
    dataTableProps?.pagination?.pageSize !== undefined &&
    dataTableProps?.pagination?.total !== undefined;

  return (
    <>
      <Panel
        {...panelProps}
        actionRow={panelActionRow}
      >
        {!groupBy.length ? (
          <DataTable
            {...dataTableProps}
            items={dataTableProps?.items ?? []}
            density={densitySetting}
            headers={
              (areHeadersConfigured
                ? getHeadersFromHeaderConfigs(configuredHeaders, headersMap)
                : getHeadersFromHeaderConfigs(
                    initialHeadersConfig,
                    headersMap
                  )) ?? []
            }
          />
        ) : (
          <>
            {dataTableProps?.items?.length || dataTableProps.isLoading ? (
              <Accordion
                onlyAllowOneOpen
                shouldApplyDefaultsWhenChanged
                defaultActiveKeys={defaultActiveGroupByGroups}
                onChange={(groups) => {
                  onActiveGroupByGroupsChange?.(groups.map((g) => String(g)));
                }}
              >
                {groupByAccordionPanels}
              </Accordion>
            ) : (
              <DataTableEmptyState
                title="No data to display"
                suggestion="Please try adjusting the grouping configuration, filters, or the page"
              />
            )}
            <div className={cx("footer", !showPagination && "no-pagination")}>
              <div style={{ flexGrow: 1 }}>
                <DropdownMenu
                  options={[
                    {
                      label: "By Value",
                      variant:
                        dataTableProps.sorting?.sortBy === "value"
                          ? "highlight"
                          : "default",
                      slotRight: ({ iconProps }) =>
                        dataTableProps.sorting?.sortBy === "value" && (
                          <Icon
                            {...iconProps}
                            variant={
                              dataTableProps.sorting?.sortDirection === "desc"
                                ? "arrow-down"
                                : "arrow-up"
                            }
                          />
                        ),
                      onClick: () => {
                        onGroupByChange?.({
                          groupBy: groupBy,
                          sortBy: "value",
                          sortDirection:
                            dataTableProps.sorting?.sortDirection === "asc" &&
                            dataTableProps.sorting?.sortBy === "value"
                              ? "desc"
                              : "asc",
                        });
                      },
                    },
                    {
                      label: "By Count",
                      variant:
                        dataTableProps.sorting?.sortBy === "count"
                          ? "highlight"
                          : "default",
                      slotRight: ({ iconProps }) =>
                        dataTableProps.sorting?.sortBy === "count" && (
                          <Icon
                            {...iconProps}
                            variant={
                              dataTableProps.sorting?.sortDirection === "desc"
                                ? "arrow-down"
                                : "arrow-up"
                            }
                          />
                        ),
                      onClick: () => {
                        onGroupByChange?.({
                          groupBy: groupBy,
                          sortBy: "count",
                          sortDirection:
                            dataTableProps.sorting?.sortDirection === "asc" &&
                            dataTableProps.sorting?.sortBy === "count"
                              ? "desc"
                              : "asc",
                        });
                      },
                    },
                  ]}
                >
                  <ButtonWithPopover
                    label="Sort"
                    buttonProps={{
                      variant: "outline",
                      icon:
                        (dataTableProps.sorting?.sortBy === "count" ||
                          dataTableProps.sorting?.sortBy === "value") &&
                        dataTableProps.sorting?.sortDirection === "asc"
                          ? "arrow-up"
                          : dataTableProps.sorting?.sortBy === "count" ||
                              dataTableProps.sorting?.sortBy === "value"
                            ? "arrow-down"
                            : "minus",
                      slotRight: () =>
                        dataTableProps?.sorting?.sortBy ? (
                          <div className={cx("indicator", "is-inline")} />
                        ) : null,
                    }}
                  >
                    Sort
                  </ButtonWithPopover>
                </DropdownMenu>
                {dataTableProps.isBusy ? (
                  <IconLoading
                    speed="fast"
                    style={{ marginLeft: 4 }}
                  />
                ) : null}
              </div>
              <div>
                {showPagination ? (
                  <Pagination
                    {...(dataTableProps?.pagination ?? {
                      page: 1,
                      pageSize: DefaultPageSizeOptions[0],
                    })}
                    pageTotal={dataTableProps?.items?.length ?? 0}
                    hasNextPage={dataTableProps?.pagination?.hasNextPage}
                    total={dataTableProps?.pagination?.total}
                    sortDirection={dataTableProps?.sorting?.sortDirection}
                    isLoading={dataTableProps?.isLoading}
                    isBusy={dataTableProps?.isBusy}
                    onChange={dataTableProps?.onPaginationChange ?? (() => {})}
                    pageSizeOptions={[10, 25]}
                    pageSizeText="Groups per page"
                  />
                ) : null}
              </div>
            </div>
          </>
        )}
      </Panel>
    </>
  );
};

function TableColumnConfigurationAction<ItemType extends ItemBaseType>({
  headers,
  configuredHeaders,
  configuredTableSource,
  headersMap,
  initialHeadersConfig,
  areHeadersConfigured,
  onChangeColumnConfiguration,
  isDefaultColumnConfigurationAvailable,
  defaultColumnConfiguration,
  companyDefaultColumnConfiguration,
  personalDefaultColumnConfiguration,
  isLoadingDefaultColumnConfiguration,
  isSaveDefaultColumnConfigurationAvailable,
  isLoadingSaveDefaultColumnConfiguration,
  onSaveColumnConfigurationAsDefault,
  isDeleteDefaultColumnConfigurationAvailable,
  isLoadingDeleteDefaultColumnConfiguration,
  onDeleteDefaultColumnConfiguration,
  onResetColumnConfiguration,
  isCompanyColumnConfigurationAvailable,
}: {
  areHeadersConfigured: boolean;
  configuredTableSource: ConfiguredDataTable["source"];
} & DataTableColumnConfigurationProps<ItemType>) {
  const [isColumnConfigurationDrawerOpen, setIsColumnConfigurationDrawerOpen] =
    useState(false);
  const initialSelectedHeaderKeys = useMemo(
    () => getSelectedHeaderKeysFromHeaders(headers ?? []),
    [headers]
  );
  const configOptions = useMemo(
    () =>
      getOptionsFromHeaderConfigs(
        mergeHeaderConfigs(
          initialHeadersConfig,
          makeMapFromHeaderConfigs(configuredHeaders)
        ),
        headersMap
      ),
    [headersMap, initialHeadersConfig, configuredHeaders]
  );
  const configOptionsValue = useMemo(
    () => getSelectedHeaderKeysFromHeaderConfig(configuredHeaders),
    [configuredHeaders]
  );

  const hasDefaultConfigurationBeenModified = useMemo(
    () =>
      checkIfHeadersConfigured(
        defaultColumnConfiguration ?? [],
        configuredHeaders
      ),
    [defaultColumnConfiguration, configuredHeaders]
  );

  const hasCompanyDefaultConfigurationBeenModified = useMemo(
    () =>
      checkIfHeadersConfigured(
        companyDefaultColumnConfiguration ?? [],
        configuredHeaders
      ),
    [companyDefaultColumnConfiguration, configuredHeaders]
  );

  return (
    <>
      <div
        key="column-config"
        className={cx("configure-columns-trigger-container")}
      >
        <ButtonWithPopover
          label="Configure Columns"
          buttonProps={{
            icon: "columns",
            iconProps: {
              size: 18,
            },
            size: "small",
            onClick: () => setIsColumnConfigurationDrawerOpen(true),
          }}
        />
        {areHeadersConfigured ? (
          <div
            className={cx(
              "are-columns-configured-indicator",
              isDefaultColumnConfigurationAvailable &&
                !hasDefaultConfigurationBeenModified &&
                "is-default"
            )}
          ></div>
        ) : null}
      </div>
      <DataTableColumnConfigurationDrawer
        title="Table Columns"
        isOpen={isColumnConfigurationDrawerOpen}
        onClose={() => setIsColumnConfigurationDrawerOpen(false)}
        isLoading={!!isLoadingDefaultColumnConfiguration}
        inputProps={{
          options: configOptions,
          value: configOptionsValue,
          defaultValue: initialSelectedHeaderKeys,
          onChange: (newHeaderKeys, modifiedOpts) => {
            onChangeColumnConfiguration({
              headers: getHeaderConfigsFromOptions(modifiedOpts, newHeaderKeys),
              source: "user",
            });
          },
          searchProps: {
            placeholder: "Search Column Name...",
          },
          isMulti: true,
          allowOptionPinning: true,
          allowReordering: true,
        }}
        actionRow={
          <div className={cx("column-configuration-drawer-actions")}>
            {isSaveDefaultColumnConfigurationAvailable &&
            onSaveColumnConfigurationAsDefault ? (
              <Button
                key="save-col-config-as-default"
                className={cx("column-configuration-drawer-actions__button")}
                variant="primary"
                onClick={() => {
                  if (onSaveColumnConfigurationAsDefault) {
                    onSaveColumnConfigurationAsDefault(configuredHeaders ?? []);
                    return;
                  }
                }}
                isLoading={
                  isLoadingDefaultColumnConfiguration ||
                  isLoadingSaveDefaultColumnConfiguration
                }
                disabled={!hasCompanyDefaultConfigurationBeenModified}
              >
                {isCompanyColumnConfigurationAvailable
                  ? "Update Company Default"
                  : "Save as Company Default"}
              </Button>
            ) : null}
            <Button
              key="reset-col-config"
              className={cx("column-configuration-drawer-actions__button")}
              variant="outline"
              onClick={() => {
                if (onResetColumnConfiguration) {
                  void onResetColumnConfiguration().then(() => {
                    if (!isCompanyColumnConfigurationAvailable) {
                      onChangeColumnConfiguration({
                        headers: initialHeadersConfig,
                        source: "code",
                      });
                    }
                  });
                  return;
                }

                onChangeColumnConfiguration({
                  headers: initialHeadersConfig,
                  source: "code",
                });
              }}
              isLoading={
                onResetColumnConfiguration !== undefined &&
                isLoadingDefaultColumnConfiguration
              }
              disabled={
                isLoadingSaveDefaultColumnConfiguration ||
                isLoadingDeleteDefaultColumnConfiguration
              }
            >
              {onResetColumnConfiguration === undefined
                ? "Reset"
                : isCompanyColumnConfigurationAvailable
                  ? "Reset to Company Default"
                  : "Reset to Application Defaults"}
            </Button>
            {isDeleteDefaultColumnConfigurationAvailable ? (
              <Button
                key="delete-default-col-config"
                className={cx("column-configuration-drawer-actions__button")}
                variant="error-outline"
                isLoading={
                  onDeleteDefaultColumnConfiguration !== undefined &&
                  (isLoadingDefaultColumnConfiguration ||
                    isLoadingDeleteDefaultColumnConfiguration)
                }
                onClick={() => {
                  if (onDeleteDefaultColumnConfiguration) {
                    onDeleteDefaultColumnConfiguration().then(() => {
                      if (configuredTableSource === "user") {
                        return;
                      }

                      if (personalDefaultColumnConfiguration?.length) {
                        onChangeColumnConfiguration({
                          headers: personalDefaultColumnConfiguration,
                          source: "user-default",
                        });
                        return;
                      }

                      onChangeColumnConfiguration({
                        headers: initialHeadersConfig,
                        source: "code",
                      });
                    });
                    return;
                  }
                }}
              >
                Delete Company Default
              </Button>
            ) : null}
          </div>
        }
      />
    </>
  );
}
